import { useAuth } from "@clerk/react";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { BlueprintList } from "@/features/blueprints/BlueprintList";
import { DeleteBlueprintDialog } from "@/features/blueprints/DeleteBlueprintDialog";
import {
  createBlueprintDraft,
  deleteBlueprint,
  listProjectBlueprints,
} from "@/features/blueprints/blueprintApi";
import type { Blueprint } from "@/features/blueprints/blueprintTypes";
import { getProject } from "@/features/projects/projectApi";
import type { Project } from "@/features/projects/projectTypes";

export function ProjectDetail() {
  const { id } = useParams<{ id: string }>();
  const { getToken, isLoaded } = useAuth();
  const navigate = useNavigate();
  const [project, setProject] = useState<Project | null>(null);
  const [blueprints, setBlueprints] = useState<Blueprint[]>([]);
  const [blueprintToDelete, setBlueprintToDelete] = useState<Blueprint | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function loadProject() {
    if (!isLoaded || !id) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const token = await getToken();
      const [projectResponse, blueprintResponse] = await Promise.all([
        getProject(token, id),
        listProjectBlueprints(token, id),
      ]);
      setProject(projectResponse.project);
      setBlueprints(blueprintResponse.blueprints);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to load project");
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    void loadProject();
  }, [isLoaded, id]);

  async function handleCreateDraft() {
    if (!id) {
      return;
    }

    setIsCreating(true);
    setError(null);

    try {
      const token = await getToken();
      const response = await createBlueprintDraft(token, {
        projectId: id,
        targetBuilder: "universal",
      });
      navigate(`/blueprints/${response.blueprint.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to create draft");
    } finally {
      setIsCreating(false);
    }
  }

  async function handleDeleteBlueprint() {
    if (!blueprintToDelete) {
      return;
    }

    setIsDeleting(true);
    setError(null);

    try {
      const token = await getToken();
      await deleteBlueprint(token, blueprintToDelete.id);
      setBlueprints((currentBlueprints) =>
        currentBlueprints.filter(
          (blueprint) => blueprint.id !== blueprintToDelete.id,
        ),
      );
      setBlueprintToDelete(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to delete draft");
    } finally {
      setIsDeleting(false);
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-28 animate-pulse rounded-lg bg-white" />
        <div className="h-32 animate-pulse rounded-lg bg-white" />
      </div>
    );
  }

  if (error && !project) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-5 text-sm text-red-800">
        {error}
      </div>
    );
  }

  if (!project) {
    return null;
  }

  return (
    <div className="space-y-8">
      <section className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <Link
            to="/projects"
            className="text-sm font-semibold text-indigo-700 hover:text-indigo-900"
          >
            Back to projects
          </Link>
          <h2 className="mt-3 break-words text-2xl font-semibold tracking-normal text-slate-950">
            {project.name}
          </h2>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600">
            Add an app idea, then generate a build-ready blueprint when you are
            ready.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void handleCreateDraft()}
          disabled={isCreating}
          className="w-fit rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-indigo-300"
        >
          {isCreating ? "Creating..." : "New app idea"}
        </button>
      </section>

      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          {error}
        </div>
      ) : null}

      <section className="space-y-4">
        <div>
          <h3 className="text-lg font-semibold text-slate-950">App ideas</h3>
          <p className="mt-1 text-sm text-slate-600">
            Each app idea becomes the starting point for a generated blueprint.
          </p>
        </div>

        {blueprints.length === 0 ? (
          <div className="rounded-lg border border-dashed border-slate-300 bg-white p-8 text-center">
            <h4 className="text-lg font-semibold text-slate-950">
              No app ideas yet
            </h4>
            <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-slate-600">
              Start an app idea draft and your input will autosave here.
            </p>
          </div>
        ) : (
          <BlueprintList
            blueprints={blueprints}
            onDelete={setBlueprintToDelete}
          />
        )}
      </section>

      <DeleteBlueprintDialog
        blueprint={blueprintToDelete}
        isDeleting={isDeleting}
        onCancel={() => setBlueprintToDelete(null)}
        onConfirm={handleDeleteBlueprint}
      />
    </div>
  );
}
